import { useInitUserQuery } from "graphql/user";
import { useIntercom } from "providers/IntercomProvider";
import { useEffect } from "react";

/**
 * Syncs the authenticated user's identity with Intercom so that identity
 * verification (user_hash) succeeds and gated content (e.g. help articles)
 * is served.
 *
 * Must be used in every authenticated route tree (admin and employee).
 * Without it, Intercom treats the session as an unverified visitor and
 * withholds user-targeted content.
 */
export const useSyncIntercomUser = (): void => {
  const { data } = useInitUserQuery();
  const { update } = useIntercom();

  const name = data?.user.profile?.name;
  const userId = data?.user.id;
  const userHash = data?.user.idHash;

  useEffect(() => {
    // Identity verification only requires user_id + user_hash. `name` is an
    // optional display attribute for Intercom, so it must NOT gate the call —
    // otherwise a user without a profile name would never be identified and
    // would lose their conversation history.
    if (!userId || !userHash) return;

    update({
      user_id: userId,
      user_hash: userHash,
      ...(name ? { name } : {}),
    });
  }, [name, userId, userHash, update]);
};
